import { camelCase } from 'lodash';
import { FRONTEND_PROPERTY_NAME, SYSTEM_NODES, } from '../../common/constants.js';
import { isValidGuid } from '../../common/utilities.js';
import { validateAnswerRequiredField } from './validate-answer-required.js';
import { validateBehaviors } from './validate-behaviors.js';
import { validateCommand } from './validate-command.js';
import { validateCommandsMetadata } from './validate-commands-metadata.js';
import { validateIntent } from './validate-intent.js';
import { validateCssClassName } from './validate-css-class-name.js';
import { validateGlobalVariables } from './validate-global-variables.js';
import { validateWhatsNext } from './validate-whats-next.js';
import { validateNluDisabled } from './validate-nlu-disabled.js';
import { validateNextNodes } from './validate-next-nodes.js';
import { validateNodeInputs } from './validate-node-inputs.js';
import { validateOutput } from './validate-output.js';
import { validateDecisionVariable } from './validate-decision-variable.js';
import { validateMessage } from './validate-message.js';
import { validateGroups } from './validate-groups.js';
import { validateEvent } from './validate-event.js';
import { validateRichAssets } from './validate-rich-assets.js';
export function validateNode(nodeFields, pifFileSummary, services, accept) {
    const systemNodes = Object.values(SYSTEM_NODES);
    const { idField, nodeTypeField, refNode } = nodeFields;
    if (!idField) {
        // We should have the id field in the node
        accept('error', `Missing required 'id' field for this node.`, {
            node: refNode,
            data: {
                nodeNumber: refNode?.name,
                propertyName: FRONTEND_PROPERTY_NAME.ID,
            },
        });
    }
    else if (!isValidGuid(idField.id.value)) {
        accept('error', `Provided id '${idField.id.value}' is not a valid GUID.`, {
            node: idField,
            data: {
                nodeNumber: refNode?.name,
                propertyName: FRONTEND_PROPERTY_NAME.ID,
            },
        });
    }
    const nodeNumber = Number(refNode.name);
    if (isNaN(nodeNumber)) {
        accept('error', `Node number '${refNode.name}' is not a valid number.`, {
            node: refNode,
            data: {
                id: idField?.id?.value,
                nodeNumber: refNode?.name,
                propertyName: FRONTEND_PROPERTY_NAME.NODE_NUMBER,
            },
        });
    }
    else {
        // Check if it falls outside the range of a 32 bit integer value
        // which is what GraphQL permits
        if (nodeNumber > 2147483647 || nodeNumber < -2147483648) {
            const flags = services.featureFlagsService.getFeatureFlags();
            const severity = flags.validation.NODE_NUMBER_OUT_OF_RANGE;
            accept(severity, `Node number value '${nodeNumber}' falls outside the allowed range of -2,147,483,648 to +2,147,483,647.`, {
                node: refNode,
                data: {
                    id: idField?.id?.value,
                    nodeNumber: refNode?.name,
                    propertyName: FRONTEND_PROPERTY_NAME.NODE_NUMBER,
                },
            });
        }
        else {
            if (nodeNumber < 0 && systemNodes.indexOf(refNode.name) === -1) {
                accept('error', `Designer defined node number '${refNode.name}' is negative. Only system nodes can have negative numbers.`, {
                    node: refNode,
                    data: {
                        id: idField?.id?.value,
                        nodeNumber: refNode?.name,
                        propertyName: FRONTEND_PROPERTY_NAME.NODE_NUMBER,
                    },
                });
            }
        }
    }
    if (!nodeTypeField) {
        accept('error', `Missing required 'nodeType' field for this node.`, {
            node: refNode,
            data: {
                nodeNumber: refNode?.name,
                propertyName: FRONTEND_PROPERTY_NAME.NODE_TYPE,
            },
        });
    }
    else if (nodeTypeField.nodeType?.value &&
        ['A', 'D'].indexOf(nodeTypeField.nodeType.value) === -1) {
        accept('error', `Node type is invalid. It should be either 'A' or 'D'.`, {
            node: nodeTypeField,
            data: {
                nodeNumber: refNode?.name,
                propertyName: FRONTEND_PROPERTY_NAME.NODE_TYPE,
            },
        });
    }
    // Verify that the fields are not duplicated
    const fieldFlags = {};
    refNode.fields.forEach((field) => {
        if (fieldFlags[field.$type]) {
            accept('error', `Duplicate field '${camelCase(field.$type)}' found in the node.`, {
                node: refNode,
                data: {
                    id: idField?.id?.value,
                    nodeNumber: refNode?.name,
                    propertyName: camelCase(field.$type),
                },
            });
        }
        fieldFlags[field.$type] = true;
    });
    if (nodeTypeField?.nodeType?.value === 'A') {
        validateCommand(refNode, nodeFields, accept);
        validateWhatsNext(refNode, nodeFields, pifFileSummary, accept);
        validateNodeInputs(refNode, nodeFields, pifFileSummary, accept);
        validateOutput(refNode, nodeFields, accept);
        validateDecisionVariable(refNode, nodeFields, accept);
        validateCommandsMetadata(refNode, nodeFields, services, accept);
    }
    else if (nodeTypeField?.nodeType?.value === 'D') {
        validateAnswerRequiredField(refNode, nodeFields, accept);
        validateNextNodes(refNode, nodeFields, accept);
        validateMessage(refNode, nodeFields, pifFileSummary, accept);
        validateRichAssets(refNode, nodeFields, pifFileSummary, accept);
        validateBehaviors(refNode, nodeFields, accept);
        validateCssClassName(refNode, nodeFields, accept);
        validateNluDisabled(refNode, nodeFields, accept);
    }
    // Run checks on fields that are present in both
    // action nodes and decision nodes
    validateIntent(refNode, nodeFields, accept);
    validateGlobalVariables(refNode, nodeFields, accept);
    validateGroups(refNode, nodeFields, accept);
    validateEvent(refNode, nodeFields, accept);
}
//# sourceMappingURL=node.js.map